import type { DataColumn, DataModel } from "../types";
import { resultTextSqlForColumn } from "./sqlExpressions";

// Columns stored in a related table (DataModelColumn.JoinKeyColumn) in the SQL preview.
//
// Mirrors ReportSqlBuilder.RelatedTables.cs - the server builds the SQL that actually runs;
// this builds the same shape for the preview, so the two must stay in step:
//   - a condition on a related column is EXISTS over the record's related rows (a negated
//     operator is NOT EXISTS of its positive form), with columns qualified rc_rel.[x] /
//     rc_src.[x] inside it;
//   - a Count / Percentage breakdown by a related column LEFT JOINs that table straight to the
//     main table (aliased rc_src, every column then qualified) and counts
//     COUNT(DISTINCT rc_src.[RecordKeyColumn]); conditions on the joined table apply per row;
//   - Full Row Detail LEFT JOINs every related table it shows the same way (rows repeat);
//   - excluded records keep one row per record and show the related values as a STRING_AGG list;
//   - a dimension containing an EXISTS is grouped through CROSS APPLY (SQL Server refuses
//     subqueries in GROUP BY).
// A model without JoinKeyColumn on any column produces exactly the preview it produced before.
//
// The preview is built synchronously, so the per-build state below is a module-level value
// set by withRelatedMode and always restored.

export const RELATED_BASE_ALIAS = "rc_src";
const INNER_BASE_ALIAS = "rc_b";
const JOIN_ALIAS_PREFIX = "rc_j";
const EXISTS_ALIAS = "rc_rel";
const EXISTS_ANCHOR_ALIAS = "rc_rel_anchor";
const LIST_ALIAS = "rc_list";
const LIST_VALUE = "rc_value";
const GROUP_APPLY_ALIAS = "rc_grp";
const MARKER_PREFIX = "/*rc_related:";
const MARKER_SUFFIX = "*/";
const MARKER_PATTERN = /\/\*rc_related:([a-z0-9_. ]+)\*\//g;

const IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/;
const TABLE = /^(\[[A-Za-z_][A-Za-z0-9_ ]*\]|[A-Za-z_][A-Za-z0-9_]*)(\.(\[[A-Za-z_][A-Za-z0-9_ ]*\]|[A-Za-z_][A-Za-z0-9_]*)){0,2}$/;

/** Negated operators read as NOT EXISTS of their positive form on a related column. */
export const RELATED_POSITIVE_OPERATORS: Record<string, string> = {
    not_in: "in",
    ne: "eq",
    not_contains: "contains",
    not_starts_with: "starts_with",
    not_ends_with: "ends_with",
    not_between: "between",
    empty: "not_empty",
};

type RelatedMode = "discover" | "fanned" | "list";

interface RelatedState {
    mode: RelatedMode;
    fanned: Set<string>;
    depth: number;
    qualifyTable: string | null;
    // List mode: related columns read as value lists, in first-use order; one LEFT JOIN each.
    listJoins: DataColumn[];
    // Fanned mode: related tables joined flat to the main table (rc_src), not in a derived table.
    flat: boolean;
}

let state: RelatedState = { mode: "list", fanned: new Set(), depth: 0, qualifyTable: null, listJoins: [], flat: false };

export function isRelatedColumn(column?: DataColumn | null): boolean {
    return !!column && !!column.joinKeyColumn?.trim() && !!column.sourceTable?.trim();
}

function modelColumns(model: DataModel): DataColumn[] {
    return [...(model.visibleColumns || []), ...model.categoryColumns, ...model.timestampColumns, ...model.otherColumns];
}

export function modelHasRelatedColumns(model?: DataModel | null) {
    return !!model && modelColumns(model).some(isRelatedColumn);
}

/** Whether the column's catalog row can be emitted for a related table (plain identifiers only). */
function isUsableRelated(column: DataColumn) {
    return TABLE.test((column.sourceTable || "").trim())
        && IDENTIFIER.test((column.joinKeyColumn || "").trim())
        && IDENTIFIER.test((column.sql || "").trim());
}

export function relatedTableKey(column: DataColumn) {
    return (column.sourceTable || "").trim().replace(/[[\]]/g, "").toLowerCase();
}

const tableSql = (column: DataColumn) => (column.sourceTable || "").trim();
const keySql = (column: DataColumn) => `[${(column.joinKeyColumn || "").trim()}]`;
const nameSql = (column: DataColumn) => `[${(column.sql || "").trim()}]`;

function isFanned(column: DataColumn) {
    return state.mode === "fanned" && isRelatedColumn(column) && state.fanned.has(relatedTableKey(column));
}

export function rowsAreFanned(model?: DataModel | null) {
    return !!model && state.mode === "fanned" && modelColumns(model).some(isFanned);
}

/** Runs one preview build in a mode, restoring the previous state. */
function withRelatedMode<T>(mode: RelatedMode, fanned: string[], build: () => T, flat = false): T {
    const previous = state;
    state = { mode, fanned: new Set(fanned), depth: 0, qualifyTable: null, listJoins: [], flat };
    try {
        return build();
    } finally {
        state = previous;
    }
}

/** Full Row Detail / excluded records: one row per record, related values listed. */
export function buildWithRelatedLists<T>(build: () => T): T {
    return withRelatedMode("list", [], build);
}

/**
 * Count / Percentage: build once marking related output values, then rebuild with their table
 * joined. More than one such table is refused with a comment, as the server refuses it.
 */
export function buildWithRelatedJoins(sectionName: string, build: () => string, allowSeveralTables = false, flatModel?: DataModel | null): string {
    const discovered = withRelatedMode("discover", [], build);
    const tables = Array.from(new Set(Array.from(discovered.matchAll(MARKER_PATTERN)).map((match) => match[1]))).sort();
    if (!tables.length) return discovered;
    if (tables.length > 1 && !allowSeveralTables) {
        return `-- "${sectionName || "Untitled metric"}": its breakdowns use columns from more than one related table (${tables.join(", ")}). ` +
            "A metric can split its rows by one related table only.";
    }
    if (!flatModel) return withRelatedMode("fanned", tables, build);
    return qualifyFlatJoinSql(withRelatedMode("fanned", tables, build, true), flatModel, new Set(tables));
}

// ---- FROM ----

/** FROM source: the table as before, aliased rc_src when the model has related columns, or the joined derived table. */
export function relatedFromSql(model: DataModel) {
    if (!modelHasRelatedColumns(model)) return model.table;
    const fanned = modelColumns(model).filter(isFanned).filter(isUsableRelated);
    if (!fanned.length) {
        // List mode: one pre-grouped LEFT JOIN per related column read as a value list, so the
        // record keeps one row (mirrors RelatedListJoinSql in ReportSqlBuilder.RelatedTables.cs).
        const columns = modelColumns(model);
        const joins = state.listJoins
            .map((column, index) => ({ column, index }))
            .filter((item) => columns.some((candidate) => isRelatedColumn(candidate)
                && relatedTableKey(candidate) === relatedTableKey(item.column)
                && candidate.sql.trim().toLowerCase() === item.column.sql.trim().toLowerCase()))
            .map((item) => relatedListJoinSql(item.column, item.index + 1));
        return [`${model.table} AS ${RELATED_BASE_ALIAS}`, ...joins].join("\n");
    }

    const byTable = new Map<string, DataColumn[]>();
    fanned.forEach((column) => {
        const key = relatedTableKey(column);
        const list = byTable.get(key) || [];
        if (!list.some((item) => item.sql.trim().toLowerCase() === column.sql.trim().toLowerCase())) list.push(column);
        byTable.set(key, list);
    });
    const select = [`${INNER_BASE_ALIAS}.*`];
    const joins: string[] = [];
    Array.from(byTable.keys()).sort().forEach((key, index) => {
        const alias = `${JOIN_ALIAS_PREFIX}${index + 1}`;
        const columns = byTable.get(key)!.slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.dataModelColumnId ?? 0) - (b.dataModelColumnId ?? 0));
        columns.forEach((column) => select.push(`${alias}.${nameSql(column)}`));
        const first = columns[0];
        joins.push(`  LEFT JOIN ${tableSql(first)} AS ${alias} ON ${alias}.${keySql(first)} = ${INNER_BASE_ALIAS}.${keySql(first)}`);
    });
    if (state.flat) {
        return [`${model.table} AS ${RELATED_BASE_ALIAS}`,
            ...joins.map((join) => join.trim().replace(` = ${INNER_BASE_ALIAS}.`, ` = ${RELATED_BASE_ALIAS}.`))].join("\n");
    }
    return `(\n  SELECT ${select.join(", ")}\n  FROM ${model.table} AS ${INNER_BASE_ALIAS}\n${joins.join("\n")}\n) AS ${RELATED_BASE_ALIAS}`;
}

// ---- conditions ----

/** Whether a predicate on this column is evaluated per record through EXISTS. */
export function needsRelatedPredicate(column?: DataColumn | null): boolean {
    return state.depth === 0 && isRelatedColumn(column) && !isFanned(column!) && isUsableRelated(column!);
}

/** Builds the predicate that goes inside an EXISTS: wrapping suspended, resolved columns qualified. */
export function qualifiedRelatedPredicate<T>(tableKey: string, build: () => T): T {
    const previousTable = state.qualifyTable;
    state.qualifyTable = tableKey;
    state.depth++;
    try {
        return build();
    } finally {
        state.depth--;
        state.qualifyTable = previousTable;
    }
}

/** A condition's column as the predicate inside an EXISTS reads it (rc_rel.[x] / rc_src.[x]). */
export function qualifiedConditionColumn<T extends DataColumn | null | undefined>(column: T): T {
    if (!column || !state.qualifyTable) return column;
    if (isRelatedColumn(column) && relatedTableKey(column) === state.qualifyTable) {
        return { ...column, sql: `${EXISTS_ALIAS}.${nameSql(column)}`, idSql: undefined } as T;
    }
    if (IDENTIFIER.test((column.sql || "").trim())) {
        return { ...column, sql: `${RELATED_BASE_ALIAS}.[${column.sql.trim()}]`, idSql: undefined } as T;
    }
    return column;
}

export function relatedExistsSql(column: DataColumn, predicate: string, negated: boolean) {
    const exists = `EXISTS (SELECT 1 FROM (VALUES (1)) AS ${EXISTS_ANCHOR_ALIAS} (rc_one) ` +
        `LEFT JOIN ${tableSql(column)} AS ${EXISTS_ALIAS} ON ${EXISTS_ALIAS}.${keySql(column)} = ${RELATED_BASE_ALIAS}.${keySql(column)} ` +
        `WHERE ${predicate})`;
    return negated ? `NOT ${exists}` : exists;
}

/** Report member / invoice exclusion lists: "<column> is not on the list". */
export function relatedListNotIn(column: DataColumn, values: string[]) {
    const list = values.map((value) => `'${String(value).replace(/'/g, "''")}'`).join(", ");
    if (needsRelatedPredicate(column)) {
        const sql = qualifiedRelatedPredicate(relatedTableKey(column), () => `${qualifiedConditionColumn(column).sql} IN (${list})`);
        return relatedExistsSql(column, sql, true);
    }
    return `(${column.sql} IS NULL OR ${column.sql} NOT IN (${list}))`;
}

/** True while a nested predicate is being built (helpers must not wrap again). */
export function insideRelatedPredicate() {
    return state.depth > 0;
}

// ---- grouping ----

// A subquery anywhere in the expression: a text calculated field reuses its input through
// (SELECT ... FROM (VALUES (...)) AS rc_t(rc_v)) - see logic/textCalculatedFields.ts.
const SUBQUERY_PATTERN = /\(\s*SELECT\b/i;

/**
 * Moves a dimension that contains an EXISTS - or any other subquery, e.g. a text calculated
 * field used as a breakdown - into a CROSS APPLY, so it can be grouped by (SQL Server refuses
 * subqueries in GROUP BY). Mirrors HoistRelatedDimension.
 */
export function hoistRelatedDimension<T extends { select: string; groupBy: string }>(dimension: T, groupApply: string[]): T {
    if (!dimension.groupBy.includes(EXISTS_ANCHOR_ALIAS) && !SUBQUERY_PATTERN.test(dimension.groupBy)) return dimension;
    const name = `g${groupApply.length + 1}`;
    groupApply.push(`${dimension.groupBy} AS [${name}]`);
    const reference = `${GROUP_APPLY_ALIAS}.[${name}]`;
    const select = dimension.select === dimension.groupBy ? reference : dimension.select.split(dimension.groupBy).join(reference);
    return { ...dimension, select, groupBy: reference };
}

export function relatedGroupApplyLines(groupApply: string[]) {
    return groupApply.length ? ["CROSS APPLY (SELECT", groupApply.map((item) => `  ${item}`).join(",\n"), `) AS ${GROUP_APPLY_ALIAS}`] : [];
}

// ---- values ----

/** The column as an output value: itself, a marked reference (first pass), or the record's value list. */
export function relatedValueSql(column: DataColumn) {
    if (!isRelatedColumn(column) || !isUsableRelated(column)) return column.sql;
    if (state.mode === "discover") return `${column.sql}${MARKER_PREFIX}${relatedTableKey(column)}${MARKER_SUFFIX}`;
    if (state.mode === "fanned") return column.sql;
    let index = state.listJoins.findIndex((item) => relatedTableKey(item) === relatedTableKey(column) && item.sql.trim().toLowerCase() === column.sql.trim().toLowerCase());
    if (index < 0) {
        state.listJoins.push(column);
        index = state.listJoins.length - 1;
    }
    return `${LIST_JOIN_PREFIX}${index + 1}.${nameSql(column)}`;
}

export function isRelatedValueList(column: DataColumn) {
    return isRelatedColumn(column) && isUsableRelated(column) && state.mode === "list";
}

const LIST_JOIN_PREFIX = "rc_l";

function relatedListJoinSql(column: DataColumn, index: number) {
    const alias = `${LIST_JOIN_PREFIX}${index}`;
    const value = `${LIST_ALIAS}.${LIST_VALUE}`;
    return [
        "LEFT JOIN (",
        `  SELECT ${LIST_ALIAS}.${keySql(column)}, STRING_AGG(CONVERT(NVARCHAR(MAX), ${resultTextSqlForColumn({ ...column, sql: value })}), N', ') WITHIN GROUP (ORDER BY ${value}) AS ${nameSql(column)}`,
        `  FROM (SELECT DISTINCT ${keySql(column)}, ${nameSql(column)} AS ${LIST_VALUE} FROM ${tableSql(column)} WHERE ${nameSql(column)} IS NOT NULL) AS ${LIST_ALIAS}`,
        `  GROUP BY ${LIST_ALIAS}.${keySql(column)}`,
        `) AS ${alias} ON ${alias}.${keySql(column)} = ${RELATED_BASE_ALIAS}.${keySql(column)}`,
    ].join("\n");
}

/** A column's value as NVARCHAR for a CASE result (mirrors ReportSqlBuilder.ResultTextSql). */
export function relatedResultTextSql(column: DataColumn) {
    return isRelatedValueList(column) ? relatedValueSql(column) : resultTextSqlForColumn({ ...column, sql: relatedValueSql(column) });
}

/** COUNT(*) / COUNT(DISTINCT col), counting each record once while a related table is joined. */
export function relatedCountAllExpr(model: DataModel | null | undefined, column: DataColumn | null) {
    if (column) return `COUNT(DISTINCT ${relatedValueSql(column)})`;
    if (!rowsAreFanned(model)) return "COUNT(*)";
    const key = (model!.recordKeyColumn || "").trim();
    return IDENTIFIER.test(key)
        ? `COUNT(DISTINCT ${RELATED_BASE_ALIAS}.[${key}])`
        : "COUNT(*) /* the data model needs a RecordKeyColumn to count each record once */";
}

// ---- flat joins ----
//
// Mirrors ReportSqlBuilder.RelatedFlatJoins.cs: once a build that joins related tables flat is
// finished, every bare catalog column name is qualified (rc_src.[x] for a main-table column,
// rc_jN.[x] for a joined related column), because the related tables share names such as the
// join key with the main table. Brackets, literals, comments, @parameters, names after a dot or
// AS, and function names are left alone.

const FLAT_RESERVED = new Set([
    "AS", "AND", "OR", "NOT", "NULL", "IS", "IN", "ON", "BY", "SELECT", "FROM", "WHERE", "GROUP", "ORDER",
    "CASE", "WHEN", "THEN", "ELSE", "END", "JOIN", "LEFT", "INNER", "OUTER", "CROSS", "APPLY", "EXISTS",
    "DISTINCT", "TOP", "BETWEEN", "LIKE", "ASC", "DESC", "OVER", "PARTITION", "UNION", "ALL", "WITH",
    "YEAR", "QUARTER", "MONTH", "WEEK", "DAY", "HOUR", "MINUTE", "SECOND", "DATE", "TIME", "DATETIME",
    "DATETIME2", "INT", "BIGINT", "DECIMAL", "FLOAT", "BIT", "VARCHAR", "NVARCHAR", "CHAR", "NCHAR", "MAX",
]);

const isIdentStart = (ch: string) => /[A-Za-z_#]/.test(ch);
const isIdentChar = (ch: string) => /[A-Za-z0-9_#$]/.test(ch);

function previousNonSpace(sql: string, index: number) {
    for (let j = index - 1; j >= 0; j--) if (!/\s/.test(sql[j])) return sql[j];
    return "";
}

function nextNonSpace(sql: string, index: number) {
    for (let j = index; j < sql.length; j++) if (!/\s/.test(sql[j])) return sql[j];
    return "";
}

export function qualifyFlatJoinSql(sql: string, model: DataModel, fannedTables: Set<string>): string {
    if (!sql) return sql;
    const columns = modelColumns(model);
    const keys = Array.from(new Set(columns
        .filter((c) => isRelatedColumn(c) && isUsableRelated(c) && fannedTables.has(relatedTableKey(c)))
        .map(relatedTableKey))).sort();
    const aliases = new Map(keys.map((key, index) => [key, `${JOIN_ALIAS_PREFIX}${index + 1}`]));

    const owner = new Map<string, string>();
    columns.filter((c) => isRelatedColumn(c) && aliases.has(relatedTableKey(c))).forEach((c) => {
        const name = (c.sql || "").trim();
        if (IDENTIFIER.test(name) && !owner.has(name)) owner.set(name, aliases.get(relatedTableKey(c))!);
    });
    [...columns.filter((c) => !isRelatedColumn(c)).map((c) => (c.sql || "").trim()), "ClientId", (model.clientColumn || "").trim(), (model.recordKeyColumn || "").trim()]
        .forEach((name) => { if (IDENTIFIER.test(name) && !owner.has(name)) owner.set(name, RELATED_BASE_ALIAS); });
    Array.from(owner.keys()).forEach((name) => { if (FLAT_RESERVED.has(name.toUpperCase())) owner.delete(name); });

    let out = "";
    let previousWord = "";
    let i = 0;
    while (i < sql.length) {
        const ch = sql[i];
        if (ch === "'") {
            let end = i + 1;
            while (end < sql.length) {
                if (sql[end] === "'" && sql[end + 1] === "'") { end += 2; continue; }
                if (sql[end] === "'") break;
                end++;
            }
            end = Math.min(end + 1, sql.length);
            out += sql.slice(i, end);
            i = end;
            previousWord = "";
            continue;
        }
        if (ch === "-" && sql[i + 1] === "-") {
            let end = sql.indexOf("\n", i);
            if (end < 0) end = sql.length;
            out += sql.slice(i, end);
            i = end;
            continue;
        }
        if (ch === "/" && sql[i + 1] === "*") {
            const close = sql.indexOf("*/", i + 2);
            const end = close < 0 ? sql.length : close + 2;
            out += sql.slice(i, end);
            i = end;
            continue;
        }
        if (ch === "[") {
            const close = sql.indexOf("]", i);
            const end = close < 0 ? sql.length : close + 1;
            const token = sql.slice(i, end);
            const prefix = `${RELATED_BASE_ALIAS}.`;
            const alias = owner.get(token.replace(/^\[|\]$/g, ""));
            if (alias && alias !== RELATED_BASE_ALIAS && out.endsWith(prefix)
                && (out.length === prefix.length || !isIdentChar(out[out.length - prefix.length - 1]))) {
                out = out.slice(0, out.length - prefix.length) + alias + ".";
            }
            out += token;
            i = end;
            previousWord = "";
            continue;
        }
        if (ch === "@") {
            let end = i + 1;
            while (end < sql.length && (isIdentChar(sql[end]) || sql[end] === "@")) end++;
            out += sql.slice(i, end);
            i = end;
            previousWord = "";
            continue;
        }
        if (isIdentStart(ch)) {
            let end = i + 1;
            while (end < sql.length && isIdentChar(sql[end])) end++;
            const word = sql.slice(i, end);
            if (word === "N" && sql[end] === "'") { out += word; i = end; continue; }
            const alias = owner.get(word);
            const after = nextNonSpace(sql, end);
            const isReference = !!alias && previousNonSpace(sql, i) !== "." && after !== "." && after !== "("
                && previousWord.toUpperCase() !== "AS" && !(i > 0 && /[0-9]/.test(sql[i - 1]));
            out += isReference ? `${alias}.[${word}]` : word;
            previousWord = word;
            i = end;
            continue;
        }
        if (!/\s/.test(ch)) previousWord = "";
        out += ch;
        i++;
    }
    return out;
}
